import { Booster, BoxScoreMLB, BoxScoreNBA, BoxScoreNFL, BoxScoreWNBA, Card, Contest } from 'models';

export type LeagueStatItem = { label: string; value: string | number };

const isPresent = (value: unknown): value is string | number => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') return value.trim() !== '' && value !== '0';
  return false;
};

const formatBoostValue = (card: Card, booster?: Booster | null): string | null => {
  if (!booster) return null;
  if (!isPresent(card.nakedAttributePoints)) return null;
  return `${booster.attributeMultiplier} × ${card.nakedAttributePoints} ${booster.attributeDisplay?.toUpperCase()}`;
};

export const mapLeagueStats = (card: Card, contest?: Contest | null, booster?: Booster | null): LeagueStatItem[] => {
  if (!contest) return [];

  const league = contest.league as 'nba' | 'wnba' | 'mlb' | 'nfl' | string;

  const stats: LeagueStatItem[] = [];

  if (league === 'nba') {
    const pts = (card.boxScore as BoxScoreNBA | undefined)?.points;
    if (isPresent(pts)) stats.push({ label: 'Points in the game', value: pts });
    const boost = formatBoostValue(card, booster);
    if (isPresent(boost)) stats.push({ label: 'Legendary Boost', value: boost });
  } else if (league === 'wnba') {
    const pts = (card.boxScore as BoxScoreWNBA | undefined)?.points;
    if (isPresent(pts)) stats.push({ label: 'Points in the game', value: pts });
    const boost = formatBoostValue(card, booster);
    if (isPresent(boost)) stats.push({ label: 'Legendary Boost', value: boost });
  } else if (league === 'mlb') {
    const box = card.boxScore as BoxScoreMLB | undefined;
    if (box && isPresent(box.coefficient?.totalBases) && isPresent(box.totalBases)) {
      stats.push({ label: 'Total Bases', value: `${box.coefficient.totalBases} x ${box.totalBases}` });
    }
    if (box && isPresent(box.coefficient?.h) && isPresent(box.h)) {
      stats.push({ label: 'Total Hits', value: `${box.coefficient.h} x ${box.h}` });
    }
    const boost = formatBoostValue(card, booster);
    if (isPresent(boost)) {
      const rarity = booster?.rarity;
      const rarityLabel = rarity ? `${rarity.charAt(0).toUpperCase()}${rarity.slice(1)}` : 'Boost';
      stats.push({
        label: `${rarityLabel} Boost`,
        value: boost,
      });
    }
  } else if (league === 'nfl') {
    const box = card.boxScore as BoxScoreNFL | undefined;
    const passing = box?.passingYds;
    if (isPresent(passing)) {
      const coeff = box?.coefficient?.['passingYds'];
      stats.push({
        label: 'Passing Yards',
        value: isPresent(coeff) ? `${coeff} x ${passing}` : passing,
      });
    }
    const rushing = box?.rushingYds;
    if (isPresent(rushing)) {
      const coeff = box?.coefficient?.['rushingYds'];
      stats.push({
        label: 'Rushing Yards',
        value: isPresent(coeff) ? `${coeff} x ${rushing}` : rushing,
      });
    }
    const receiving = box?.receivingYds;
    if (isPresent(receiving)) {
      const coeff = box?.coefficient?.['receivingYds'];
      stats.push({
        label: 'Receiving Yards',
        value: isPresent(coeff) ? `${coeff} x ${receiving}` : receiving,
      });
    }
    const boost = formatBoostValue(card, booster);
    if (isPresent(boost))
      stats.push({
        label: `${booster?.rarity?.charAt(0).toUpperCase()}${booster?.rarity?.slice(1)} Boost`,
        value: boost,
      });
  }

  if (isPresent(card.modifierPoints)) {
    stats.push({ label: 'Modifier points', value: card.modifierPoints as number });
  }

  return stats;
};
